<template>
  <div class="il-summary">
    <div
      ref="summaryWrapper"
      class="il-summary__wrapper"
      :style="{ height: contentHeight }"
    >
      <transition
        mode="out-in"
        :css="false"
        @before-leave="onBeforeLeave"
        @leave="onLeave"
        @before-enter="onBeforeEnter"
        @enter="onEnter"
        @after-enter="onAfterEnter"
      >

        <!-- ── SINGLE / MULTIPLE ──────────────────────────────────────────── -->
        <div v-if="getBet.type === 'single' || getBet.type === 'multiple'" key="single-multiple" class="il-section">

          <div class="il-row">
            <div class="il-label">{{ $t('amount') }} {{ getSettings.currencySymbol }}</div>
            <div class="il-input-wrap">
              <button class="il-stepper" :disabled="getBet._multiple_detail.amount <= 0" @click="decMultiple">
                <div class="il-stepper__inner"><svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor" viewBox="0 0 16 16"><path d="M4 8a.5.5 0 0 1 .5-.5h7a.5.5 0 0 1 0 1h-7A.5.5 0 0 1 4 8"/></svg></div>
              </button>
              <input
                autocomplete="off"
                :value="getBet._multiple_detail.amount"
                type="number"
                step="0.50"
                min="0"
                class="il-input"
                @input="onMultipleAmountInput"
                @blur="onMultipleAmountBlur"
              />
              <button class="il-stepper" @click="incMultiple">
                <div class="il-stepper__inner"><svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor" viewBox="0 0 16 16"><path fill-rule="evenodd" d="M8 2a.5.5 0 0 1 .5.5v5h5a.5.5 0 0 1 0 1h-5v5a.5.5 0 0 1-1 0v-5h-5a.5.5 0 0 1 0-1h5v-5A.5.5 0 0 1 8 2"/></svg></div>
              </button>
            </div>
          </div>

          <div v-if="getSettings.enableButtonsCart" class="il-quick-amounts">
            <button
              v-for="(btn, i) in getSettings.buttonsCart"
              :key="i"
              class="il-quick-btn"
              @click="addAmountMultiple(btn.value)"
            >+{{ btn.value }} {{ getSettings.currencySymbol }}</button>
          </div>

          <div v-if="getSettings.sm_multiplicity" class="il-row">
            <div class="il-label-sm">{{ $t('multiplicity') }}</div>
            <div class="il-value-sm">{{ getBet._multiple_detail.multiplicity }}</div>
          </div>

          <div v-if="getSettings.sm_bonus && +getBet._multiple_detail.bonus_percentage > 0" class="il-row">
            <div class="il-label-sm">{{ $t('bonus') }}</div>
            <div class="il-value-sm">% {{ getBet._multiple_detail.bonus_percentage }}</div>
          </div>

          <div v-if="getSettings.sm_odds_total && !hasBBRedundant" class="il-row">
            <div class="il-label-sm">{{ $t('odds') }}</div>
            <div class="il-value-sm">x {{ getBet._multiple_detail.odds_total | round_digits(2) | odds_type_filter }}</div>
          </div>

          <div v-if="getSettings.sm_winning && !hasBBRedundant" class="il-row">
            <div class="il-label-sm">{{ $t('winning') }}</div>
            <div class="il-value-sm">{{ getSettings.currencySymbol }} {{ getBet._multiple_detail.winning | round_digits(2) }}</div>
          </div>

          <div v-if="getSettings.sm_bonus_total && +getBet._multiple_detail.bonus > 0 && !hasBBRedundant" class="il-row">
            <div class="il-label-sm">{{ $t('bonus') }} {{ $t('total') }}</div>
            <div class="il-value-sm">{{ getSettings.currencySymbol }} {{ getBet._multiple_detail.bonus | round_digits(2) }}</div>
          </div>

          <div v-if="getSettings.sm_profit && !hasBBRedundant" class="il-row">
            <div class="il-label-sm">{{ $t('profit') }}</div>
            <div class="il-value-sm">{{ getSettings.currencySymbol }} {{ getBet._multiple_detail.profit | round_digits(2) }}</div>
          </div>

          <div v-if="getSettings.sm_winning_total && !hasBBRedundant" class="il-row il-row--highlight">
            <div class="il-label-lg">{{ $t('winning') }} {{ $t('total') }}</div>
            <div class="il-value-lg">{{ getSettings.currencySymbol }} {{ getBet._multiple_detail.winning_total | round_digits(2) }}</div>
          </div>

          <template v-if="getPercentageTaxValueOnWon > 0">
            <div class="il-row">
              <div class="il-label-sm">{{ $t('percentageTaxValueOnWon') }}</div>
              <div class="il-value-sm">{{ getPercentageTaxValueOnWon + ' %' }}</div>
            </div>
            <div v-if="getBet._multiple_detail.expectedTotalTaxedWinnings" class="il-row">
              <div class="il-label-sm">{{ $t('expected_winning_total_taxed') }}</div>
              <div class="il-value-sm">{{ getSettings.currencySymbol }} {{ getBet._multiple_detail.expectedTotalTaxedWinnings | round_digits(2) }}</div>
            </div>
            <div class="il-row">
              <div class="il-label-sm">{{ $t('expectedProfitTaxed') }}</div>
              <div class="il-value-sm">{{ getSettings.currencySymbol }} {{ getBet._multiple_detail.expectedProfitTaxed | round_digits(2) }}</div>
            </div>
          </template>

        </div>

        <!-- ── INTEGRAL ───────────────────────────────────────────────────── -->
        <div v-else-if="getBet.type === 'integral'" key="integral" class="il-section">

          <div class="il-row">
            <div class="il-label">
              {{ getSettings.integralDivided ? $t('amountTotal') : $t('amount_per_combination') }}
              {{ getSettings.currencySymbol }}
            </div>
            <div class="il-input-wrap">
              <button class="il-stepper" :disabled="getBet._integral_detail.amount <= 0" @click="decIntegral">
                <div class="il-stepper__inner"><svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor" viewBox="0 0 16 16"><path d="M4 8a.5.5 0 0 1 .5-.5h7a.5.5 0 0 1 0 1h-7A.5.5 0 0 1 4 8"/></svg></div>
              </button>
              <input
                autocomplete="off"
                :value="getBet._integral_detail.amount"
                type="number"
                step="0.50"
                class="il-input"
                @input="onIntegralAmountInput"
              />
              <button class="il-stepper" @click="incIntegral">
                <div class="il-stepper__inner"><svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor" viewBox="0 0 16 16"><path fill-rule="evenodd" d="M8 2a.5.5 0 0 1 .5.5v5h5a.5.5 0 0 1 0 1h-5v5a.5.5 0 0 1-1 0v-5h-5a.5.5 0 0 1 0-1h5v-5A.5.5 0 0 1 8 2"/></svg></div>
              </button>
            </div>
          </div>

          <div v-if="getSettings.enableButtonsCart" class="il-quick-amounts">
            <button
              v-for="(btn, i) in getSettings.buttonsCart"
              :key="i"
              class="il-quick-btn"
              @click="addAmountIntegral(btn.value)"
            >+{{ btn.value }} {{ getSettings.currencySymbol }}</button>
          </div>

          <div v-if="getSettings.in_combinations" class="il-row">
            <div>
              <div class="il-label-sm">{{ $t('amountTotal') }}</div>
              <div class="il-hint">{{ getSettings.integralDivided ? $t('total_equals_amount') : $t('total_equals_amount_x_combinations') }}</div>
            </div>
            <div class="il-value-sm">{{ amountTotalComputed | round_digits(2) }}</div>
          </div>

          <div v-if="getSettings.in_combinations" class="il-row">
            <div class="il-label-sm">{{ $t('combinations') }}</div>
            <div class="il-value-sm">{{ getBet._integral_detail.combinations }}</div>
          </div>

          <div v-if="getSettings.in_odds_total_min" class="il-row">
            <div class="il-label-sm">{{ $t('odds') }} {{ $t('total') }} {{ $t('min') }}</div>
            <div class="il-value-sm">x {{ getBet._integral_detail.odds_total_min | round_digits(2) | odds_type_filter }}</div>
          </div>

          <div v-if="getSettings.in_odds_total_max" class="il-row">
            <div class="il-label-sm">{{ $t('odds') }} {{ $t('total') }} {{ $t('max') }}</div>
            <div class="il-value-sm">x {{ getBet._integral_detail.odds_total_max | round_digits(2) | odds_type_filter }}</div>
          </div>

          <div v-if="getSettings.in_bonus_min && +getBet._integral_detail.bonus_min > 0" class="il-row">
            <div class="il-label-sm">{{ $t('bonus') }} {{ $t('min') }}</div>
            <div class="il-value-sm">{{ getSettings.currencySymbol }} {{ getBet._integral_detail.bonus_min | round_digits(2) }}</div>
          </div>

          <div v-if="getSettings.in_bonus_max && +getBet._integral_detail.bonus_max > 0" class="il-row">
            <div class="il-label-sm">{{ $t('bonus') }} {{ $t('max') }}</div>
            <div class="il-value-sm">{{ getSettings.currencySymbol }} {{ getBet._integral_detail.bonus_max | round_digits(2) }}</div>
          </div>

          <div v-if="getSettings.in_profit_min" class="il-row">
            <div class="il-label-sm">{{ $t('profit') }} {{ $t('min') }}</div>
            <div class="il-value-sm">{{ getSettings.currencySymbol }} {{ getBet._integral_detail.profit_min | round_digits(2) }}</div>
          </div>

          <div v-if="getSettings.in_profit_max" class="il-row">
            <div class="il-label-sm">{{ $t('profit') }} {{ $t('max') }}</div>
            <div class="il-value-sm">{{ getSettings.currencySymbol }} {{ getBet._integral_detail.profit_max | round_digits(2) }}</div>
          </div>

          <div v-if="getSettings.in_winning_total_min" class="il-row">
            <div class="il-label-sm">{{ $t('winning') }} {{ $t('total') }} {{ $t('min') }}</div>
            <div class="il-value-sm">{{ getSettings.currencySymbol }} {{ getBet._integral_detail.winning_total_min | round_digits(2) }}</div>
          </div>

          <div v-if="getSettings.in_winning_total_max" class="il-row">
            <div class="il-label-sm">{{ $t('winning') }} {{ $t('total') }} {{ $t('max') }}</div>
            <div class="il-value-sm">{{ getSettings.currencySymbol }} {{ getBet._integral_detail.winning_total_max | round_digits(2) }}</div>
          </div>

          <div v-if="getSettings.in_winning_min" class="il-row il-row--highlight">
            <div class="il-label-lg">{{ $t('winning') }} {{ $t('min') }}</div>
            <div class="il-value-lg">{{ getSettings.currencySymbol }} {{ getBet._integral_detail.winning_min | round_digits(2) }}</div>
          </div>

          <div v-if="getSettings.in_winning_max" class="il-row il-row--highlight">
            <div class="il-label-lg">{{ $t('winning') }} {{ $t('max') }}</div>
            <div class="il-value-lg">{{ getSettings.currencySymbol }} {{ getBet._integral_detail.winning_max | round_digits(2) }}</div>
          </div>

          <template v-if="getPercentageTaxValueOnWon > 0">
            <div class="il-row">
              <div class="il-label-sm">{{ $t('percentageTaxValueOnWon') }}</div>
              <div class="il-value-sm">{{ getPercentageTaxValueOnWon + ' %' }}</div>
            </div>
            <div class="il-row">
              <div class="il-label-sm">{{ $t('profit_min_taxed') }}</div>
              <div class="il-value-sm">{{ getSettings.currencySymbol }} {{ getBet._integral_detail.profit_min_taxed | round_digits(2) }}</div>
            </div>
            <div class="il-row">
              <div class="il-label-sm">{{ $t('profit_max_taxed') }}</div>
              <div class="il-value-sm">{{ getSettings.currencySymbol }} {{ getBet._integral_detail.profit_max_taxed | round_digits(2) }}</div>
            </div>
            <div class="il-row">
              <div class="il-label-sm">{{ $t('winning_total_min_taxed') }}</div>
              <div class="il-value-sm">{{ getSettings.currencySymbol }} {{ getBet._integral_detail.winning_total_min_taxed | round_digits(2) }}</div>
            </div>
            <div class="il-row">
              <div class="il-label-sm">{{ $t('winning_total_max_taxed') }}</div>
              <div class="il-value-sm">{{ getSettings.currencySymbol }} {{ getBet._integral_detail.winning_total_max_taxed | round_digits(2) }}</div>
            </div>
          </template>

        </div>

        <!-- ── SYSTEM ──────────────────────────────────────────────────────── -->
        <div v-else-if="getBet.type === 'system'" key="system" class="il-section">

          <div class="il-row">
            <div class="il-label">{{ $t('amount') }} {{ $t('all') }} {{ getSettings.currencySymbol }}</div>
            <div class="il-input-wrap">
              <button class="il-stepper" :disabled="amountSystemEqual <= 0" @click="decAmountSystemEqual">
                <div class="il-stepper__inner"><svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor" viewBox="0 0 16 16"><path d="M4 8a.5.5 0 0 1 .5-.5h7a.5.5 0 0 1 0 1h-7A.5.5 0 0 1 4 8"/></svg></div>
              </button>
              <input autocomplete="off" :value="amountSystemEqual" type="number" step="0.50" class="il-input" @change="setAmountSystemEqual($event.target.value)" />
              <button class="il-stepper" @click="incAmountSystemEqual">
                <div class="il-stepper__inner"><svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor" viewBox="0 0 16 16"><path fill-rule="evenodd" d="M8 2a.5.5 0 0 1 .5.5v5h5a.5.5 0 0 1 0 1h-5v5a.5.5 0 0 1-1 0v-5h-5a.5.5 0 0 1 0-1h5v-5A.5.5 0 0 1 8 2"/></svg></div>
              </button>
            </div>
          </div>

          <div class="il-row">
            <div class="il-label">{{ $t('amount') }} {{ $t('divide') }} {{ getSettings.currencySymbol }}</div>
            <div class="il-input-wrap">
              <button class="il-stepper" :disabled="amountSystemToDivide <= 0" @click="decAmountSystemToDivide">
                <div class="il-stepper__inner"><svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor" viewBox="0 0 16 16"><path d="M4 8a.5.5 0 0 1 .5-.5h7a.5.5 0 0 1 0 1h-7A.5.5 0 0 1 4 8"/></svg></div>
              </button>
              <input autocomplete="off" :value="amountSystemToDivide" type="number" step="0.50" class="il-input" @change="setAmountSystemToDivide($event.target.value)" />
              <button class="il-stepper" @click="incAmountSystemToDivide">
                <div class="il-stepper__inner"><svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor" viewBox="0 0 16 16"><path fill-rule="evenodd" d="M8 2a.5.5 0 0 1 .5.5v5h5a.5.5 0 0 1 0 1h-5v5a.5.5 0 0 1-1 0v-5h-5a.5.5 0 0 1 0-1h5v-5A.5.5 0 0 1 8 2"/></svg></div>
              </button>
            </div>
          </div>

          <!-- Combos header -->
          <div class="il-combos-header">
            <div>{{ $t('type') }}</div>
            <div>N°</div>
            <div class="il-combos-header__amount">{{ $t('amount') }}</div>
          </div>

          <!-- Combos rows -->
          <div v-for="(combo, i) in systemCombosValid" :key="'sys_' + i" class="il-combo">
            <div class="il-combo__top">
              <span class="il-combo__label">
                <span v-if="combo.multiplicity <= 5">{{ $t('multi-' + combo.multiplicity) }}</span>
                <span v-else>{{ combo.multiplicity }}{{ $t('-multi') }}</span>
              </span>
              <div class="il-combo__right">
                <div class="il-combo__count">x{{ combo.valid_combinations }}</div>
                <div class="il-input-wrap il-input-wrap--sm">
                  <button class="il-stepper il-stepper--sm" :disabled="combo.amount <= 0" @click="decComboAmount(combo)">
                    <div class="il-stepper__inner"><svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16"><path d="M4 8a.5.5 0 0 1 .5-.5h7a.5.5 0 0 1 0 1h-7A.5.5 0 0 1 4 8"/></svg></div>
                  </button>
                  <input :disabled="combo.valid_combinations === 0" :value="combo.amount" type="number" step="0.50" class="il-input il-input--sm" @change="onComboAmountChange(combo, $event.target.value)" />
                  <button class="il-stepper il-stepper--sm" @click="incComboAmount(combo)">
                    <div class="il-stepper__inner"><svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16"><path fill-rule="evenodd" d="M8 2a.5.5 0 0 1 .5.5v5h5a.5.5 0 0 1 0 1h-5v5a.5.5 0 0 1-1 0v-5h-5a.5.5 0 0 1 0-1h5v-5A.5.5 0 0 1 8 2"/></svg></div>
                  </button>
                </div>
              </div>
            </div>
            <div v-if="combo.amount > 0" class="il-combo__detail">
              <div class="il-combo__detail-row">{{ $t('amount') }}: <strong>{{ getSettings.currencySymbol }}{{ combo.totalAmount | round_digits(2) }}</strong></div>
              <div class="il-combo__detail-row">{{ $t('win') }} {{ $t('min') }}: <strong>{{ getSettings.currencySymbol }}{{ combo.winning_min | round_digits(2) }}</strong></div>
              <div class="il-combo__detail-row">{{ $t('win') }} {{ $t('max') }}: <strong>{{ getSettings.currencySymbol }}{{ combo.winning_all | round_digits(2) }}</strong></div>
            </div>
          </div>

          <div v-if="getSettings.sys_combinations" class="il-row">
            <div class="il-label-sm">{{ $t('combinations') }}</div>
            <div class="il-value-sm">{{ getBet._system_detail.combinations }}</div>
          </div>

          <div v-if="getSettings.sys_amount_total" class="il-row">
            <div class="il-label-sm">{{ $t('amount') }} {{ $t('total') }} {{ $t('system') }}</div>
            <div class="il-value-sm">{{ getSettings.currencySymbol }} {{ getBet._system_detail.amount_system | round_digits(2) }}</div>
          </div>

          <div v-if="getSettings.sys_bonus_min && +getBet._system_detail.bonus_min > 0" class="il-row">
            <div class="il-label-sm">{{ $t('bonus') }} {{ $t('min') }}</div>
            <div class="il-value-sm">{{ getSettings.currencySymbol }} {{ getBet._system_detail.bonus_min | round_digits(2) }}</div>
          </div>

          <div v-if="getSettings.sys_winning_min" class="il-row">
            <div class="il-label-sm">{{ $t('winning') }} {{ $t('min') }}</div>
            <div class="il-value-sm">{{ getSettings.currencySymbol }} {{ getBet._system_detail.winning_min | round_digits(2) }}</div>
          </div>

          <div v-if="getSettings.sys_winning_total_min" class="il-row">
            <div class="il-label-sm">{{ $t('winning') }} {{ $t('total') }} {{ $t('min') }}</div>
            <div class="il-value-sm">{{ getSettings.currencySymbol }} {{ getBet._system_detail.winning_total_min | round_digits(2) }}</div>
          </div>

          <div v-if="getSettings.sys_bonus_max && +getBet._system_detail.bonus_max > 0" class="il-row">
            <div class="il-label-sm">{{ $t('bonus') }} {{ $t('max') }}</div>
            <div class="il-value-sm">{{ getSettings.currencySymbol }} {{ getBet._system_detail.bonus_max | round_digits(2) }}</div>
          </div>

          <div v-if="getSettings.sys_winning_max" class="il-row">
            <div class="il-label-sm">{{ $t('winning') }} {{ $t('max') }}</div>
            <div class="il-value-sm">{{ getSettings.currencySymbol }} {{ getBet._system_detail.winning_max | round_digits(2) }}</div>
          </div>

          <div v-if="getSettings.sys_winning_total_max" class="il-row">
            <div class="il-label-sm">{{ $t('winning') }} {{ $t('total') }} {{ $t('max') }}</div>
            <div class="il-value-sm">{{ getSettings.currencySymbol }} {{ getBet._system_detail.winning_total_max | round_digits(2) }}</div>
          </div>

          <div v-if="getSettings.sys_winning_all_total" class="il-row il-row--highlight">
            <div class="il-label-lg">{{ $t('winning') }} {{ $t('all') }} {{ $t('total') }}</div>
            <div class="il-value-lg">{{ getSettings.currencySymbol }} {{ getBet._system_detail.winning_all_total | round_digits(2) }}</div>
          </div>

          <template v-if="getPercentageTaxValueOnWon > 0">
            <div class="il-row">
              <div class="il-label-sm">{{ $t('percentageTaxValueOnWon') }}</div>
              <div class="il-value-sm">{{ getPercentageTaxValueOnWon + ' %' }}</div>
            </div>
            <div class="il-row">
              <div class="il-label-sm">{{ $t('winning_total_min_taxed') }}</div>
              <div class="il-value-sm">{{ getSettings.currencySymbol }} {{ getBet._system_detail.winning_total_min_taxed | round_digits(2) }}</div>
            </div>
            <div class="il-row">
              <div class="il-label-sm">{{ $t('winning_total_max_taxed') }}</div>
              <div class="il-value-sm">{{ getSettings.currencySymbol }} {{ getBet._system_detail.winning_total_max_taxed | round_digits(2) }}</div>
            </div>
            <div class="il-row">
              <div class="il-label-sm">{{ $t('winning_all_total_taxed') }}</div>
              <div class="il-value-sm">{{ getSettings.currencySymbol }} {{ getBet._system_detail.winning_all_total_taxed | round_digits(2) }}</div>
            </div>
          </template>

        </div>

      </transition>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SummaryAmerican',
  data() {
    return {
      contentHeight: 'auto',
    };
  },
  computed: {
    getBet() { return this.$store.getters['cart/getBet']; },
    getSettings() { return this.$store.getters['cart/getSettings']; },
    getBetSystemParts() { return this.$store.getters['cart/getBetSystemParts'] || []; },
    getPercentageTaxValueOnWon() { return this.$store.getters['settings/getPercentageTaxValueOnWon'] || 0; },
    hasBBRedundant() {
      return (this.getBet.odds || []).some(o => o.bb_redundant === true || o.bb_unavailable === true);
    },
    systemCombosValid() {
      return this.getBetSystemParts.filter(c => c && c.valid_combinations > 0);
    },
    amountTotalComputed() {
      const amount = +this.getBet._integral_detail.amount || 0;
      const combinations = +this.getBet._integral_detail.combinations || 1;
      return this.getSettings.integralDivided ? amount : amount * combinations;
    },
    amountSystemEqual() {
      return this.$store.state.cart.bet._system_detail.amount_system_equal;
    },
    amountSystemToDivide() {
      return this.$store.state.cart.bet._system_detail.amount_system_to_divide;
    },
  },
  methods: {
    async refreshCart() {
      await this.$store.dispatch('cart/refreshCart');
    },
    // ── Height animation hooks ───────────────────────────────────────────
    onBeforeLeave() {
      const wrapper = this.$refs.summaryWrapper;
      if (wrapper) this.contentHeight = wrapper.scrollHeight + 'px';
    },
    onLeave(el, done) {
      el.style.transition = 'opacity 0.2s ease-out';
      el.style.opacity = '0';
      setTimeout(done, 200);
    },
    onBeforeEnter(el) {
      el.style.opacity = '0';
    },
    onEnter(el, done) {
      this.$nextTick(() => {
        const newHeight = el.scrollHeight;
        const wrapper = this.$refs.summaryWrapper;
        if (wrapper) { void wrapper.offsetHeight; }
        this.contentHeight = newHeight + 'px';
        setTimeout(() => {
          el.style.transition = 'opacity 0.2s ease-out';
          el.style.opacity = '1';
          setTimeout(done, 200);
        }, 250);
      });
    },
    onAfterEnter(el) {
      this.contentHeight = 'auto';
      el.style.transition = '';
      el.style.opacity = '';
    },
    // ── Multiple ─────────────────────────────────────────────────────────
    async onMultipleAmountInput(e) {
      let val = +e.target.value;
      if (val < 0) val = 0;
      this.getBet._multiple_detail.amount = val;
      await this.refreshCart();
    },
    async onMultipleAmountBlur() {
      const min = this.getSettings.minAmount || 0;
      if (+this.getBet._multiple_detail.amount < min) {
        this.getBet._multiple_detail.amount = min;
        await this.refreshCart();
      }
    },
    async incMultiple() {
      this.getBet._multiple_detail.amount = parseFloat((+(this.getBet._multiple_detail.amount || 0) + 0.5).toFixed(2));
      await this.refreshCart();
    },
    async decMultiple() {
      this.getBet._multiple_detail.amount = parseFloat(Math.max(0, +(this.getBet._multiple_detail.amount || 0) - 0.5).toFixed(2));
      await this.refreshCart();
    },
    async addAmountMultiple(amount) {
      if (+amount > 0) {
        this.getBet._multiple_detail.amount = parseFloat((+(this.getBet._multiple_detail.amount || 0) + +amount).toFixed(2));
        await this.refreshCart();
      }
    },
    // ── Integral ─────────────────────────────────────────────────────────
    async onIntegralAmountInput(e) {
      let val = +e.target.value;
      if (val < 0) val = 0;
      this.getBet._integral_detail.amount = val;
      await this.refreshCart();
    },
    async incIntegral() {
      this.getBet._integral_detail.amount = parseFloat((+(this.getBet._integral_detail.amount || 0) + 0.5).toFixed(2));
      await this.refreshCart();
    },
    async decIntegral() {
      this.getBet._integral_detail.amount = parseFloat(Math.max(0, +(this.getBet._integral_detail.amount || 0) - 0.5).toFixed(2));
      await this.refreshCart();
    },
    async addAmountIntegral(amount) {
      if (+amount > 0) {
        this.getBet._integral_detail.amount = parseFloat((+(this.getBet._integral_detail.amount || 0) + +amount).toFixed(2));
        await this.refreshCart();
      }
    },
    // ── System ───────────────────────────────────────────────────────────
    async setAmountSystemEqual(value) {
      const min = this.getSettings.minAmount || 0;
      const amount = Math.max(min, +value || 0);
      const systemParts = this.getBet.system_parts || [];
      const allUnchecked = systemParts.every(p => !p.checked);
      await this.$store.commit('cart/SET_SYSTEM_AMOUNT_TO_DIVIDE', null);
      await this.$store.commit('cart/SET_SYSTEM_AMOUNT_EQUAL', amount);
      if (allUnchecked) await this.checkAllComboSystem();
      else await this.refreshCart();
    },
    async setAmountSystemToDivide(value) {
      const min = this.getSettings.minAmount || 0;
      const amount = Math.max(min, +value || 0);
      const systemParts = this.getBet.system_parts || [];
      const allUnchecked = systemParts.every(p => !p.checked);
      await this.$store.commit('cart/SET_SYSTEM_AMOUNT_EQUAL', null);
      await this.$store.commit('cart/SET_SYSTEM_AMOUNT_TO_DIVIDE', amount);
      if (allUnchecked) await this.checkAllComboSystem();
      else await this.refreshCart();
    },
    async incAmountSystemEqual() {
      const next = parseFloat((+(this.amountSystemEqual || 0) + 0.5).toFixed(2));
      await this.setAmountSystemEqual(next);
    },
    async decAmountSystemEqual() {
      const next = parseFloat(Math.max(0, +(this.amountSystemEqual || 0) - 0.5).toFixed(2));
      await this.setAmountSystemEqual(next);
    },
    async incAmountSystemToDivide() {
      const next = parseFloat((+(this.amountSystemToDivide || 0) + 0.5).toFixed(2));
      await this.setAmountSystemToDivide(next);
    },
    async decAmountSystemToDivide() {
      const next = parseFloat(Math.max(0, +(this.amountSystemToDivide || 0) - 0.5).toFixed(2));
      await this.setAmountSystemToDivide(next);
    },
    async onComboAmountChange(combo, value) {
      combo.amount = Math.max(0, +value || 0);
      await this.refreshSystemCart(combo);
    },
    async incComboAmount(combo) {
      combo.amount = parseFloat((+(combo.amount || 0) + 0.5).toFixed(2));
      await this.refreshSystemCart(combo);
    },
    async decComboAmount(combo) {
      combo.amount = parseFloat(Math.max(0, +(combo.amount || 0) - 0.5).toFixed(2));
      await this.refreshSystemCart(combo);
    },
    async refreshSystemCart(combo) {
      await this.$store.dispatch('cart/setIsCartUpdating', true);
      try {
        if (combo) {
          this.getBet._system_detail.amount_system_equal = null;
          this.getBet._system_detail.amount_system_to_divide = null;
          combo.checked = combo.amount > 0;
        }
        await this.$store.dispatch('cart/setSystemComboDetail', { system_parts: this.getBet.system_parts });
        await this.refreshCart();
      } finally {
        await this.$store.dispatch('cart/setIsCartUpdating', false);
      }
    },
    async checkAllComboSystem() {
      const systemParts = this.getBet.system_parts || [];
      systemParts.forEach(p => { if (p && p.valid_combinations > 0) p.checked = true; });
      await this.$store.dispatch('cart/setSystemComboDetail', { system_parts: this.getBet.system_parts });
      await this.refreshCart();
    },
  },
  mounted() {
    this.$root.$on('forceUpdateSingleMultiple', () => this.$forceUpdate());
  },
};
</script>

<style scoped>
/* ── Wrapper con transizione altezza ─────────────────────────────────────── */
.il-summary { padding: 0 8px 4px; }
.il-summary__wrapper {
  overflow: hidden;
  transition: height 0.25s ease-out;
  will-change: height;
}

/* ── Section (ogni tipo di bet) ──────────────────────────────────────────── */
.il-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px 0 2px;
}

/* ── Row generico label + value ──────────────────────────────────────────── */
.il-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.il-row--highlight {
  border-top: 1px solid #0000001a;
  padding-top: 8px;
  margin-top: 4px;
}

/* ── Labels ──────────────────────────────────────────────────────────────── */
.il-label {
  font-size: var(--font-size-3xl);
  font-weight: 600;
  color: var(--text-cart, #ffffff);
}
.il-label-sm {
  font-size: var(--font-size-3xl);
  color: var(--text-cart, #ffffff);
}
.il-label-lg {
  font-size: var(--font-size-6xl);
  font-weight: 700;
  color: var(--text-cart, #ffffff);
}
.il-hint {
  font-size: var(--font-size-xl);
  color: var(--text-op-50-platform, rgba(255,255,255,0.5));
}

/* ── Values ──────────────────────────────────────────────────────────────── */
.il-value-sm {
  font-size: var(--font-size-3xl);
  font-weight: 600;
  color: var(--text-cart, #ffffff);
  text-align: right;
}
.il-value-lg {
  font-size: var(--font-size-7xl);
  font-weight: 700;
  color: var(--text-cart, #ffffff);
}

/* ── Input wrapper (Illinois: border border-illinois-border-dark w-48) ───── */
.il-input-wrap {
  display: inline-flex;
  align-items: center;
  border: 1px solid #444444;
  border-radius: 4px;
  width: 160px;
  flex-shrink: 0;
  overflow: hidden;
}
.il-input-wrap:focus-within {
  border-color: var(--color-primary);
  font-weight: 600;
}
.il-input-wrap--sm { width: 120px; }

/* ── Stepper button (Illinois: h-10 w-10 border border-illinois-bg-card) ─── */
.il-stepper {
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  flex-shrink: 0;
}
.il-stepper:disabled { cursor: not-allowed; }
.il-stepper__inner {
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  width: 40px;
  height: 40px;
  color: var(--color-primary);
}
.il-stepper:disabled .il-stepper__inner { color: rgba(255,255,255,0.3); }
.il-stepper__inner:hover { background: rgba(255,255,255,0.05); }
.il-stepper--sm .il-stepper__inner { width: 32px; height: 32px; }

/* ── Input ────────────────────────────────────────────────────────────────── */
.il-input {
  flex: 1;
  text-align: center;
  width: 100%;
  height: 40px;
  border: none;
  outline: none;
  background: transparent;
  font-size: var(--font-size-3xl);
  color: var(--text-cart, #ffffff);
  padding: 0 4px;
  -moz-appearance: textfield;
}
.il-input::-webkit-outer-spin-button,
.il-input::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
.il-input--sm { height: 32px; }

/* ── Quick amount buttons ────────────────────────────────────────────────── */
.il-quick-amounts {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.il-quick-btn {
  background: none;
  border: 1px solid #444444;
  border-radius: 4px;
  font-size: var(--font-size-3xl);
  color: var(--text-platform, #ffffff);
  padding: 4px 8px;
  cursor: pointer;
  transition: background 0.15s;
}
.il-quick-btn:hover { background: rgba(255,255,255,0.05); }

/* ── System combos ───────────────────────────────────────────────────────── */
.il-combos-header {
  display: grid;
  grid-template-columns: 1fr auto auto;
  gap: 8px;
  align-items: center;
  font-size: var(--font-size-xl);
  font-weight: 600;
  color: var(--text-op-50-platform, rgba(255,255,255,0.5));
  padding: 6px 0;
  border-top: 1px solid #0000001a;
  border-bottom: 1px solid #0000001a;
}
.il-combos-header__amount { min-width: 120px; text-align: right; }

.il-combo {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 8px 0;
  border-bottom: 1px solid #0000001a;
}
.il-combo__top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.il-combo__label { font-size: var(--font-size-3xl); color: var(--text-platform, #ffffff); }
.il-combo__right {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}
.il-combo__count { font-size: var(--font-size-3xl); color: var(--text-op-50-platform, rgba(255,255,255,0.5)); }
.il-combo__detail { text-align: right; }
.il-combo__detail-row {
  font-size: var(--font-size-xl);
  color: var(--text-op-50-platform, rgba(255,255,255,0.5));
}
.il-combo__detail-row strong { color: var(--text-platform, #ffffff); }
</style>
